import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProtocols } from '../../hooks/useProtocols';
import { useProtocolCategories } from '../../hooks/useNomenclatures';
import { Plus, Search, Filter, Eye, Edit2, Trash2, GitBranch } from 'lucide-react';
import './ProtocolList.css';

/**
 * Protocol List Component
 * Displays all infection prevention protocols in a filterable grid
 */
const ProtocolList = ({ onCreateNew, onEdit, onView, onDelete, onVersionControl, showHeader = false }) => {
  const { t } = useTranslation('protocols');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // Show all statuses by default
  const [showFilters, setShowFilters] = useState(false);

  const { protocols, loading, error, refresh } = useProtocols({
    category: categoryFilter || undefined,
    status: statusFilter || undefined,
    searchTerm: searchTerm || undefined
  });

  // Load nomenclatures for proper display
  const { items: protocolCategories } = useProtocolCategories();

  // Protocol statuses (labels translated)
  const protocolStatuses = [
    { id: 'Active', name: t('active') },
    { id: 'Draft', name: t('draft') },
    { id: 'Under Review', name: t('underReview') },
    { id: 'Deprecated', name: t('deprecated') }
  ];

  // Helper: protocol.category is stored as category code (e.g. "EC") or sometimes id; match by both
  const getCategoryName = (categoryValue) => {
    if (!categoryValue || !protocolCategories) return t('unknown');
    const category = protocolCategories.find(
      cat => cat.id === categoryValue || (cat.code && String(cat.code) === String(categoryValue))
    );
    return category ? category.name : t('unknown');
  };

  const getStatusName = (status) => {
    return status || t('unknown');
  };

  const getStatusColor = (status) => {
    const statusName = (status || '').toLowerCase();
    if (statusName.includes('active')) return 'status-active';
    if (statusName.includes('draft')) return 'status-draft';
    if (statusName.includes('review')) return 'status-review';
    if (statusName.includes('deprecated')) return 'status-deprecated';
    return 'status-draft';
  };

  const getTotalActions = (protocol) => {
    const steps = protocol.steps;
    if (!Array.isArray(steps)) return 0;
    return steps.reduce((sum, step) => sum + (step.actions?.length || 0), 0);
  };

  const handleDelete = async (protocolId, protocolName) => {
    // Delegate confirmation to parent component
    if (onDelete) {
      onDelete(protocolId, protocolName);
    }
  };

  if (loading) {
    return (
      <div className="protocol-list-loading">
        <div className="spinner"></div>
        <p>{t('loadingProtocols')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="protocol-list-error">
        <div className="error-icon">⚠️</div>
        <h3>{t('errorLoadingProtocols')}</h3>
        <p>{error.message}</p>
        <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
      </div>
    );
  }

  return (
    <div className="protocol-list">
      {/* Header - only show if showHeader prop is true */}
      {showHeader && (
        <div className="protocol-list-header">
          <div>
            <h1>{t('infectionPreventionProtocols')}</h1>
            <p className="subtitle">{t('configureAndManage')}</p>
          </div>
          {onCreateNew && (
            <button onClick={onCreateNew} className="btn btn-primary">
              <Plus size={20} />
              {t('createNewProtocol')}
            </button>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="protocol-filters">
        <div className="search-box">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t('searchProtocols')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn btn-secondary ${showFilters ? 'active' : ''}`}
        >
          <Filter size={18} />
          {t('filters')}
        </button>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-group">
              <label>{t('category')}</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">{t('allCategories')}</option>
                {protocolCategories && protocolCategories.map(cat => (
                  <option key={cat.id} value={cat.code || cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>{t('status')}</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">{t('allStatuses')}</option>
                {protocolStatuses.map(status => (
                  <option key={status.id} value={status.id}>{status.name}</option>
                ))}
              </select>
            </div>

            <button
              onClick={() => {
                setCategoryFilter('');
                setStatusFilter('Active');
                setSearchTerm('');
              }}
              className="btn btn-secondary"
            >
              {t('clearFilters')}
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="results-info">
        <span className="results-count">
          {t('protocolsFound', { count: protocols.length })}
        </span>
      </div>

      {/* Protocol Table */}
      {protocols.length > 0 ? (
        <div className="protocol-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th colSpan={2}>{t('tableName')}</th>
                <th>{t('tableCategory')}</th>
                <th>{t('tableStatus')}</th>
                <th>{t('tableVersion')}</th>
                <th>{t('tablePSteps')}</th>
                <th>{t('tablePActions')}</th>
                <th>{t('tableDuration')}</th>
                <th>{t('tableActions')}</th>
              </tr>
            </thead>
            <tbody>
              {protocols.map((protocol, index) => (
                <tr key={protocol.firestoreId || `protocol-${index}`}>
                  <td colSpan={2}>
                    <div className="name-cell">
                      <strong>{protocol.name}</strong>
                      {!protocol.structuralCriticalityLevelId && (
                        <span className="protocol-badge protocol-badge-incomplete" title={t('incompleteNoCriticality')}>
                          {t('incompleteNoCriticality')}
                        </span>
                      )}
                      {protocol.shortName && (
                        <div className="short-name">{protocol.shortName}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">
                      {getCategoryName(protocol.category)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(protocol.status)}`}>
                      {protocolStatuses.find(s => s.id === protocol.status)?.name ?? getStatusName(protocol.status)}
                    </span>
                  </td>
                  <td>
                    <span className="version-badge">v{protocol.version}</span>
                  </td>
                  <td>
                    <span className="steps-badge">
                      {protocol.steps?.length || 0} {t('steps')}
                    </span>
                  </td>
                  <td>
                    <span className="protocol-actions-badge">
                      {getTotalActions(protocol)} {t('actions')}
                    </span>
                  </td>
                  <td>
                    <span className="duration-badge">
                      {protocol.totalEstimatedDuration || 0}min
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-secondary btn-icon"
                        onClick={() => onView(protocol)}
                        title={t('view')}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="btn btn-edit btn-icon"
                        onClick={() => onEdit(protocol)}
                        title={t('edit')}
                      >
                        <Edit2 size={16} />
                      </button>
                      {onVersionControl && (
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={() => onVersionControl(protocol)}
                          title={t('versions')}
                        >
                          <GitBranch size={16} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          className="btn btn-delete btn-icon"
                          onClick={() => handleDelete(protocol.firestoreId || protocol.id, protocol.name)}
                          title={t('delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>{t('noProtocolsFound')}</h3>
          <p>{t('createFirstProtocol')}</p>
          <button onClick={onCreateNew} className="btn btn-primary">
            <Plus size={20} />
            {t('createProtocol')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProtocolList;

